/**
 * 健康习惯 —— 版本路由外壳（P0-0.1，见 docs/health-habit-v2-feasibility.md §6）
 *
 * 职责只有三件：选版本、扛降级、记埋点。
 * 不做任何数据判定（D12 不变式：前端不得决定"这个用户属于哪种情况"）。
 *
 * 兼容性约束：
 * - 对外 props 与出口路径保持不变（record/index.jsx 仍 import '@/pages/user/components/HealthHabitView'），
 *   因此那个 102 KB 的巨石文件 0 改动（R10）。
 * - V1 组件原样冻结在 ./legacy，内部逻辑一行未改（D1/D4）。
 * - V2 尚未进包时（P1-1.7 之前）不渲染任何切换控件 ⇒ 现网视觉零变化。
 */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Segmented, Space, Tooltip } from 'antd';
import { getHealthHabitVersion } from '@/services/healthHabitVersion';
import HealthHabitLegacyView from './legacy';
import {
  SUPPORTED_SCHEMA_VERSION,
  VIEW_V1,
  VIEW_V2,
  canOfferV2,
  normalizeVersionStatus,
  readPreference,
  resolveViewVersion,
  writePreference,
} from './versionPolicy';
import { getViewComponent } from './versions';
import { trackVersionSwitch } from './tracking';

const getStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (error) {
    return null;
  }
};

/**
 * V2 渲染抛错时兜住并回退 V1（§6 自动降级行）。
 * V1 抛错不套用本边界：那是 V1 自己的既有行为，P0 承诺零改动。
 */
class V2ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    this.props.onError(error);
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

V2ErrorBoundary.propTypes = {
  onError: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default function HealthHabitView({ userId }) {
  const storage = useMemo(() => getStorage(), []);
  const [status, setStatus] = useState(null);
  const [preference, setPreference] = useState(() => readPreference(storage));
  const [degraded, setDegraded] = useState(null); // { reason } | null

  const v2Bundled = useMemo(() => Boolean(getViewComponent(VIEW_V2)), []);

  useEffect(() => {
    // 包里没有 V2，就没必要问服务端"能不能用 V2"（D13：一刀切，唯一的开关就是这次前端发布本身）。
    // P1-1.7 把 V2 注册进 versions.js 之前，这条请求必然失败 —— 不发，别留死流量。
    if (!v2Bundled) return null;
    let mounted = true;
    getHealthHabitVersion({
      requestData: { userId, schemaVersion: SUPPORTED_SCHEMA_VERSION },
    }).then((result) => {
      if (mounted) setStatus(normalizeVersionStatus(result));
    });
    return () => {
      mounted = false;
    };
  }, [userId, v2Bundled]);

  useEffect(() => {
    // 换被查看用户 = 重新开始：上一个用户的降级态不该跟过来，
    // 否则 resolveViewVersion 会一直短路在 V1，新用户连试的机会都没有。
    setDegraded(null);
  }, [userId]);

  const decision = resolveViewVersion({
    v2Bundled,
    status,
    preference,
    degraded: Boolean(degraded),
  });
  const version = decision.version;
  // 切换入口一旦有资格出现，降级后仍保留（只是禁选 V2，改由 Alert 的「重试新版」复位），避免页面结构跳动
  const offerSwitcher = canOfferV2({ v2Bundled, status, degraded: false });
  const v2Blocked = Boolean(degraded);

  const switchTo = (next, trigger = 'user', reason = null) => {
    const from = version;
    if (next !== VIEW_V1 && next !== VIEW_V2) return;
    if (next === VIEW_V2 && v2Blocked) return;
    if (next === VIEW_V1) setDegraded(null);
    writePreference(storage, next);
    setPreference(readPreference(storage));
    trackVersionSwitch({
      from,
      to: next,
      userId,
      source: 'LOCAL_OVERRIDE',
      trigger,
      reason,
    });
  };

  const handleV2Error = (error) => {
    // 只取错误类型名做归因，不带 message：内部错误文本不应泄落到页面或埋点属性
    const reason = `RENDER_ERROR${error && error.name ? `:${error.name}` : ''}`;
    const from = version;
    setDegraded({ reason });
    trackVersionSwitch({
      from,
      to: VIEW_V1,
      userId,
      source: 'DEGRADED',
      trigger: 'auto_degrade',
      reason,
    });
  };

  /** P1-1.7 的 V2 组件在契约校验失败时调用它（§6：非 00000 / 契约非法 → 回退 V1） */
  const handleV2Degrade = (reason) => {
    const from = version;
    setDegraded({ reason: reason || 'CONTRACT_INVALID' });
    trackVersionSwitch({
      from,
      to: VIEW_V1,
      userId,
      source: 'DEGRADED',
      trigger: 'auto_degrade',
      reason: reason || 'CONTRACT_INVALID',
    });
  };

  /**
   * 降级后当场重试（R2：没有服务端开关，这是唯一不必刷新页面的恢复路径）。
   * 没有这个入口时会卡死：降级已把 version 置为 V1，Segmented 的选中值也就是 V1，
   * 再点"旧版展示"不触发 onChange，`setDegraded(null)` 在页面上就没有任何可达路径。
   * 这里只清降级标志——偏好本就指向 V2（或无偏好时服务端默认给 V2），
   * resolveViewVersion 会重新选到 V2；边界组件随 V1/V2 分支切换而重建，无需额外复位令牌。
   */
  const retryV2 = () => {
    const from = version;
    const reason = degraded ? degraded.reason : null;
    setDegraded(null);
    trackVersionSwitch({
      from,
      to: VIEW_V2,
      userId,
      source: 'LOCAL_OVERRIDE',
      trigger: 'retry',
      reason,
    });
  };

  const ActiveView = getViewComponent(version) || getViewComponent(VIEW_V1);

  if (!offerSwitcher) {
    // 无切换资格（V2 未进包 / 服务端未开 / 状态未就绪）：与改造前完全一致
    return <HealthHabitLegacyView userId={userId} />;
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {/*
       * 唯一的版本切换入口（D13）。sticky 是必需的：V2 有四张维度卡 + 42 天日历轨，
       * 一屏放不下，跟着页面滚走之后"切得过去切不回来"（除了刷新）。
       * 背景与层级要给足，否则滚到卡片上会透字、会被卡片盖住。
       *
       * ！这个 Segmented 外面不要再套 Popover/Tooltip 一类 click 触发的浮层（曾经的"首次引导"
       * 就是那么套的）：rc-trigger 对 click 触发会把子节点冒泡上来的 click 直接 preventDefault，
       * 而 rc-segmented 的 onChange 只由 <label> 里那个隐藏 radio input 触发 —— 一旦 preventDefault，
       * label 的激活行为被取消，input 收不到点击，切换就彻底静默（不报错、不换值、不写偏好）。
       * 要放引导，放到这个 div 外面，或换成 hover / 受控而不吃 click 的容器。
       */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '6px 0',
          background: '#fff',
        }}
      >
        <Segmented
          size="small"
          value={version}
          onChange={(next) => switchTo(next)}
          options={[
            { label: '旧版展示', value: VIEW_V1 },
            {
              label: (
                <Tooltip
                  title={v2Blocked ? '新版暂不可用，已回退旧版' : undefined}
                >
                  <span>新版（实验）</span>
                </Tooltip>
              ),
              value: VIEW_V2,
              // antd Segmented 的禁用项由 options[].disabled 控制
              disabled: v2Blocked,
            },
          ]}
        />
      </div>

      {degraded ? (
        <Alert
          type="warning"
          showIcon
          message="新版暂不可用，已回退旧版展示"
          description={`原因代码：${degraded.reason}。数据本身未受影响。`}
          action={
            <Button size="small" onClick={retryV2}>
              重试新版
            </Button>
          }
        />
      ) : null}

      {version === VIEW_V2 ? (
        <V2ErrorBoundary onError={handleV2Error}>
          <ActiveView
            userId={userId}
            schemaVersion={SUPPORTED_SCHEMA_VERSION}
            onDegrade={handleV2Degrade}
          />
        </V2ErrorBoundary>
      ) : (
        <ActiveView userId={userId} />
      )}
    </Space>
  );
}

HealthHabitView.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
